import { Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import { Text } from "@react-three/drei";
import { Group, Mesh, Vector3 } from "three";
import type React from "react";
import type { CutDirection, Note } from "../game/beatmap";
import { checkCollision } from "../game/collision";
import { TARGET_STRIKE_Z } from "../game/constants";
import { evaluateTutorialCut } from "../game/tutorialCheck";
import {
  CAMPAIGN_TARGET_DEFAULTS,
  createTargetSpec,
  DODGE_TARGET_SPEC,
} from "../game/targetSystem";
import {
  getTutorialByPhase,
  type GhostDemo,
  type Tutorial,
  type TutorialGhostFrame,
  type TutorialPracticeCheck,
} from "../game/tutorials";
import { useGameStore } from "../game/useGameStore";
import { GhostBlade } from "../weapons/GhostBlade";
import { unlockRegisteredTracks } from "../audio/useTrack";
import { colors, primaryButton, secondaryButton, typography } from "./theme";
import {
  ShojiBacking,
  PanelText,
  PanelRule,
  PanelColumn,
  PanelErrorBoundary,
  PanelIllustration,
  prefetchPanelImage,
} from "./panel3d";

const TEXT_PROPS = {
  renderOrder: 2101,
  "material-depthTest": false,
} as const;
// V1: the ghost-sword demo is disabled; keep the component paths for v2.
const GHOST_DEMO_ENABLED = false;

const DIR_LABELS: Record<CutDirection, string> = {
  left: "←",
  right: "→",
  up: "↑",
  down: "↓",
  "up-left": "↖",
  "up-right": "↗",
  "down-left": "↙",
  "down-right": "↘",
  any: "CUT",
};

const PRACTICE_LABELS: Record<
  NonNullable<TutorialPracticeCheck["kind"]>,
  string
> = {
  cut: "CUT",
  "dodge-left": "DODGE LEFT",
  "dodge-right": "DODGE RIGHT",
  duck: "DUCK",
};

const DODGE_X = 0.18;
const DUCK_Y = 1.34;
const ACTOR_ASSETS = {
  theDoorOpens: "/assets/actors/the-door-opens-masaru.JPG",
  noSparring: "/assets/actors/no-sparring-seiji.webp",
  seijiPortrait: "/assets/actors/seiji-dialog-portrait.JPG",
  oneKatana: "/assets/actors/one-katana.webp",
  likeItMatters: "/assets/actors/like-it-matters.webp",
  chudanHonestCut: "/assets/actors/chudan-an-honest-cut.webp",
  honestCutTriptych: "/assets/actors/an-honest-cut-triptico.webp",
  theFirstCut: "/assets/actors/the-first-cut.webp",
  doNotClash: "/assets/actors/do-not-clash.webp",
  howTheDojoJudges: "/assets/actors/how-the-dojo-judges.webp",
  theBladeRemembers: "/assets/actors/the-blade-remembers.webp",
  threeWords: "/assets/actors/three-words.webp",
  firstRepetition: "/assets/actors/first-repetition.webp",
} as const;

const LESSON_ASSETS = {
  default: "/assets/lessons/default.webp",
  theSeed: "/assets/lessons/the-seed.webp",
  genzaAsAWound: "/assets/lessons/genza-as-a-wound.webp",
  memorial: "/assets/lessons/memorial.webp",
  graduation: "/assets/lessons/graduation.webp",
} as const;

function getLessonStagingImage(phase: number): string {
  if (phase === 3) return LESSON_ASSETS.theSeed;
  if (phase === 7) return LESSON_ASSETS.genzaAsAWound;
  if (phase === 8) return LESSON_ASSETS.memorial;
  if (phase === 10) return LESSON_ASSETS.graduation;
  return LESSON_ASSETS.default;
}

function TutorialPracticeTarget({ tutorial }: { tutorial: Tutorial }) {
  const phase = useGameStore((s) => s.phase);
  const resetKey = useGameStore((s) => s.tutorialPracticeResetKey);
  const recordTutorialAttempt = useGameStore((s) => s.recordTutorialAttempt);
  const checks =
    tutorial.training.checks ??
    (tutorial.training.check ? [tutorial.training.check] : []);
  const [practiceIndex, setPracticeIndex] = useState(0);
  const check = checks[practiceIndex];
  const targetRef = useRef<Mesh | null>(null);
  const targetPos = useRef(new Vector3());
  const cooldownUntil = useRef(0);
  const { camera } = useThree();

  useEffect(() => {
    setPracticeIndex(0);
    cooldownUntil.current = 0;
  }, [tutorial.phase, resetKey]);

  function recordFeedback(ok: boolean, headline: string, detail: string) {
    recordTutorialAttempt({ ok, headline, detail });
    if (ok) {
      setPracticeIndex((index) => Math.min(index + 1, checks.length));
    }
  }

  useFrame(({ clock }) => {
    if (
      phase !== "consolidate" ||
      !check ||
      practiceIndex >= checks.length ||
      !targetRef.current
    ) {
      return;
    }

    const now = clock.elapsedTime;
    if (now < cooldownUntil.current) return;

    const kind = check.kind ?? "cut";
    targetRef.current.getWorldPosition(targetPos.current);
    const hit = checkCollision(targetPos.current, 0.3);
    if (kind !== "cut") {
      if (hit) {
        recordFeedback(
          false,
          "もう一度 · again",
          "When you see danger, move. Do not swing.",
        );
        hit.pulse?.("MISS", false);
        cooldownUntil.current = now + 0.9;
        return;
      }

      const moved =
        (kind === "dodge-left" && camera.position.x < -DODGE_X) ||
        (kind === "dodge-right" && camera.position.x > DODGE_X) ||
        (kind === "duck" && camera.position.y < DUCK_Y);

      if (!moved) return;

      recordFeedback(true, "そう · that's it", "You moved instead of cutting.");
      cooldownUntil.current = now + 0.9;
      return;
    }

    if (!hit) return;

    const feedback = evaluateTutorialCut(tutorial, hit, check);
    recordFeedback(feedback.ok, feedback.headline, feedback.detail);
    hit.pulse?.(feedback.ok ? "PERFECT" : "MISS", false);
    cooldownUntil.current = now + 0.9;
  });

  if (phase !== "consolidate" || !check || practiceIndex >= checks.length) {
    return null;
  }

  const kind = check.kind ?? "cut";
  const isDanger = kind !== "cut";
  const targetColor = isDanger ? "#E0463C" : colors.amber;
  const targetText = isDanger ? PRACTICE_LABELS[kind] : DIR_LABELS[check.dir];

  return (
    <group position={[check.laneX, check.height, -0.72]}>
      <mesh ref={targetRef}>
        {isDanger ? (
          <boxGeometry args={[0.34, 0.72, 0.06]} />
        ) : (
          <sphereGeometry args={[0.18, 24, 16]} />
        )}
        <meshStandardMaterial
          color={targetColor}
          emissive={targetColor}
          emissiveIntensity={0.9}
          transparent
          opacity={0.44}
          depthWrite={false}
        />
      </mesh>
      <mesh scale={[1.35, 1.35, 1.35]}>
        <torusGeometry args={[0.18, 0.008, 8, 40]} />
        <meshBasicMaterial
          color={colors.gold}
          transparent
          opacity={0.72}
          depthWrite={false}
        />
      </mesh>
      <Text
        {...TEXT_PROPS}
        position={[0, 0, 0.02]}
        fontSize={isDanger || check.dir === "any" ? 0.055 : 0.15}
        color={colors.washi}
        anchorX="center"
        anchorY="middle"
        textAlign="center"
      >
        {targetText}
      </Text>
      <Text
        {...TEXT_PROPS}
        position={[0, -0.32, 0]}
        fontSize={0.045}
        color={colors.steel}
        anchorX="center"
        anchorY="middle"
        maxWidth={0.7}
        textAlign="center"
      >
        {check.label}
      </Text>
      {check.prompt && (
        <Text
          {...TEXT_PROPS}
          position={[0, -0.43, 0]}
          fontSize={0.034}
          color={colors.washi}
          anchorX="center"
          anchorY="middle"
          maxWidth={0.82}
          textAlign="center"
          lineHeight={1.18}
        >
          {check.prompt}
        </Text>
      )}
    </group>
  );
}

function getTeachDemoCheck(
  tutorial: Tutorial,
  lineIndex: number,
): TutorialPracticeCheck | null {
  const checks =
    tutorial.training.checks ??
    (tutorial.training.check ? [tutorial.training.check] : []);

  if (tutorial.phase === 0) {
    if (lineIndex <= 1) return null;
    if (lineIndex === 2) return checks[0] ?? null;
    if (lineIndex === 3) return checks[1] ?? null;
    return null;
  }

  return checks[0] ?? null;
}

function DemoTarget({ check }: { check: TutorialPracticeCheck }) {
  const ref = useRef<Mesh | null>(null);
  const kind = check.kind ?? "cut";
  const isDanger = kind !== "cut";
  const label = isDanger ? PRACTICE_LABELS[kind] : DIR_LABELS[check.dir];
  const color = isDanger ? "#E0463C" : "#46A6C6";
  const core = isDanger ? "#FFB3A5" : "#B9F7FF";

  useFrame(({ clock }) => {
    if (!ref.current) return;
    const t = (clock.elapsedTime % 3.2) / 3.2;
    const impact = Math.exp(-Math.pow((t - 0.58) / 0.12, 2));
    const s = 1 + impact * 0.24 + Math.sin(clock.elapsedTime * 3.5) * 0.025;
    ref.current.scale.setScalar(s);
  });

  return (
    <group position={[check.laneX, check.height, -0.86]}>
      <mesh ref={ref}>
        {isDanger ? (
          <boxGeometry args={[0.5, 0.9, 0.08]} />
        ) : (
          <sphereGeometry args={[0.2, 28, 18]} />
        )}
        <meshStandardMaterial
          color={color}
          emissive={core}
          emissiveIntensity={1.05}
          transparent
          opacity={isDanger ? 0.46 : 0.58}
          depthWrite={false}
        />
      </mesh>
      <mesh rotation={[0, 0, Math.PI / 2]}>
        <torusGeometry args={[isDanger ? 0.36 : 0.24, 0.009, 8, 48]} />
        <meshBasicMaterial
          color={isDanger ? "#FF6A5C" : colors.gold}
          transparent
          opacity={0.82}
          depthWrite={false}
        />
      </mesh>
      {!isDanger && (
        <mesh rotation={[0, 0, -0.72]}>
          <boxGeometry args={[0.03, 0.78, 0.012]} />
          <meshBasicMaterial
            color={colors.gold}
            transparent
            opacity={0.72}
            depthWrite={false}
          />
        </mesh>
      )}
      <Text
        {...TEXT_PROPS}
        position={[0, 0, 0.05]}
        fontSize={isDanger || check.dir === "any" ? 0.06 : 0.16}
        color={colors.washi}
        anchorX="center"
        anchorY="middle"
        textAlign="center"
      >
        {label}
      </Text>
    </group>
  );
}

const DEMO_STEP_SECONDS = 2.7;
const DEMO_LEAD_SECONDS = 0.55;
const DEMO_TAIL_SECONDS = 0.9;
const DEMO_APPROACH_Z = -3.4;
const DEMO_TARGET_Z = TARGET_STRIKE_Z;
const DEMO_TIP_Z = TARGET_STRIKE_Z - 0.08;
const DEMO_GRIP_Z = -0.52;

interface TutorialDemoStep {
  check: TutorialPracticeCheck;
  index: number;
  start: number;
  impact: number;
  end: number;
}

function getTutorialChecks(tutorial: Tutorial): TutorialPracticeCheck[] {
  if (tutorial.training.checks) return tutorial.training.checks;
  return tutorial.training.check ? [tutorial.training.check] : [];
}

function isDangerCheck(check: TutorialPracticeCheck) {
  return (check.kind ?? "cut") !== "cut";
}

function cutVectorForDirection(dir: CutDirection): [number, number] {
  const vector: [number, number] =
    dir === "left"
      ? [-1, 0]
      : dir === "right"
        ? [1, 0]
        : dir === "up"
          ? [0, 1]
          : dir === "down"
            ? [0, -1]
            : dir === "up-left"
              ? [-1, 1]
              : dir === "up-right"
                ? [1, 1]
                : dir === "down-left"
                  ? [-1, -1]
                  : dir === "down-right"
                    ? [1, -1]
                    : [0.58, -1];
  const length = Math.hypot(vector[0], vector[1]) || 1;
  return [vector[0] / length, vector[1] / length];
}

function buildTutorialDemoSteps(tutorial: Tutorial): TutorialDemoStep[] {
  const checks = getTutorialChecks(tutorial);
  const availableSeconds = Math.max(1.2, tutorial.ghostDemo.loopSeconds - 0.8);
  const stepSeconds =
    checks.length > 0 ? availableSeconds / checks.length : DEMO_STEP_SECONDS;

  return checks.map((check, index) => {
    const start = DEMO_LEAD_SECONDS + index * stepSeconds;
    return {
      check,
      index,
      start,
      impact: start + stepSeconds * 0.45,
      end: start + stepSeconds * 0.86,
    };
  });
}

function getDemoTargetDefaults(tutorialPhase: number) {
  if (tutorialPhase <= 0) return CAMPAIGN_TARGET_DEFAULTS[0];
  return (
    CAMPAIGN_TARGET_DEFAULTS[
      Math.min(tutorialPhase - 1, CAMPAIGN_TARGET_DEFAULTS.length - 1)
    ] ?? CAMPAIGN_TARGET_DEFAULTS[0]
  );
}

function demoDurationForSteps(steps: TutorialDemoStep[]) {
  const last = steps[steps.length - 1];
  return (last?.end ?? 1.4) + DEMO_TAIL_SECONDS;
}

function gripForTip(x: number, y: number): [number, number, number] {
  return [x * 0.52, y - 0.28, DEMO_GRIP_Z];
}

function movementForDanger(
  kind: NonNullable<TutorialPracticeCheck["kind"]>,
): [number, number] {
  if (kind === "dodge-right") return [0.42, 0];
  if (kind === "duck") return [0, -0.26];
  return [-0.42, 0];
}

function buildTutorialGhostDemo(
  tutorial: Tutorial,
  steps: TutorialDemoStep[],
): GhostDemo {
  const frames: TutorialGhostFrame[] = [
    {
      t: 0,
      tip: [0, 1.58, DEMO_TIP_Z],
      grip: [0, 1.22, DEMO_GRIP_Z],
      label: "guard",
      kamae: "chudan",
    },
  ];

  for (const step of steps) {
    const { check } = step;
    const kind = check.kind ?? "cut";

    if (kind !== "cut") {
      const [moveX, moveY] = movementForDanger(kind);
      frames.push(
        {
          t: Math.max(0, step.start - 0.25),
          tip: [0, 1.58, DEMO_TIP_Z],
          grip: [0, 1.22, DEMO_GRIP_Z],
          label: "guard",
          kamae: "chudan",
        },
        {
          t: step.impact,
          tip: [moveX, 1.58 + moveY, DEMO_TIP_Z],
          grip: [moveX * 0.9, 1.22 + moveY, DEMO_GRIP_Z],
          label: "move",
        },
        {
          t: step.end,
          tip: [moveX, 1.58 + moveY, DEMO_TIP_Z],
          grip: [moveX * 0.9, 1.22 + moveY, DEMO_GRIP_Z],
          label: "safe",
        },
      );
      continue;
    }

    const [vx, vy] = cutVectorForDirection(check.dir);
    const reach = 0.52;
    const startX = check.laneX - vx * reach;
    const startY = check.height - vy * reach;
    const finishX = check.laneX + vx * reach;
    const finishY = check.height + vy * reach;

    frames.push(
      {
        t: Math.max(0, step.start - 0.25),
        tip: [startX, startY, DEMO_TIP_Z],
        grip: gripForTip(startX, startY),
        label: "ready",
        kamae: tutorial.phase === 1 ? "jodan" : "chudan",
      },
      {
        t: step.impact,
        tip: [check.laneX, check.height, DEMO_TIP_Z],
        grip: gripForTip(check.laneX, check.height),
        label: "cut",
      },
      {
        t: step.end,
        tip: [finishX, finishY, DEMO_TIP_Z],
        grip: gripForTip(finishX, finishY),
        label: "finish",
      },
    );
  }

  return {
    title: tutorial.ghostDemo.title,
    description: tutorial.ghostDemo.description,
    loopSeconds: demoDurationForSteps(steps),
    frames: frames.sort((a, b) => a.t - b.t),
  };
}

function smoothStep(value: number) {
  const t = Math.max(0, Math.min(1, value));
  return t * t * (3 - 2 * t);
}

function TutorialDemoTarget({
  step,
  tutorialPhase,
  timeRef,
}: {
  step: TutorialDemoStep;
  tutorialPhase: number;
  timeRef: React.MutableRefObject<number>;
}) {
  const groupRef = useRef<Group | null>(null);
  const bodyRef = useRef<Group | null>(null);
  const splitRef = useRef<Group | null>(null);
  const topHalfRef = useRef<Mesh | null>(null);
  const bottomHalfRef = useRef<Mesh | null>(null);
  const slashRef = useRef<Group | null>(null);
  const check = step.check;
  const kind = check.kind ?? "cut";
  const isDanger = kind !== "cut";
  const label = isDanger ? PRACTICE_LABELS[kind] : DIR_LABELS[check.dir];
  const note = useMemo<Note>(
    () => ({
      t: step.impact,
      laneX: check.laneX,
      height: check.height,
      dir: check.dir,
      hand: "any",
    }),
    [check.dir, check.height, check.laneX, step.impact],
  );
  const target = useMemo(
    () => createTargetSpec(note, getDemoTargetDefaults(tutorialPhase)),
    [note, tutorialPhase],
  );
  const color = isDanger ? DODGE_TARGET_SPEC.color : target.color;
  const core = isDanger ? DODGE_TARGET_SPEC.coreColor : target.coreColor;
  const accent = isDanger ? DODGE_TARGET_SPEC.accent : target.accent;
  const shadow = isDanger ? DODGE_TARGET_SPEC.shadow : target.shadow;
  const bodySize = isDanger ? 0.52 : target.bodySize;
  const [vx, vy] = cutVectorForDirection(check.dir);
  const slashRotation = -Math.atan2(vx, vy);

  useFrame(() => {
    const group = groupRef.current;
    if (!group) return;

    const elapsed = timeRef.current;
    const visibleFrom = step.start - 0.75;
    const visibleTo = isDanger ? step.end + 0.65 : step.end + 0.5;
    const visible = elapsed >= visibleFrom && elapsed <= visibleTo;
    group.visible = visible;
    if (!visible) return;

    const approach = smoothStep(
      (elapsed - visibleFrom) / Math.max(0.001, step.impact - visibleFrom),
    );
    const impact = Math.exp(-Math.pow((elapsed - step.impact) / 0.12, 2));
    const afterImpact = Math.max(0, elapsed - step.impact);
    const passThrough = smoothStep(afterImpact / (isDanger ? 0.58 : 0.34));
    const z =
      elapsed <= step.impact
        ? DEMO_APPROACH_Z + (DEMO_TARGET_Z - DEMO_APPROACH_Z) * approach
        : DEMO_TARGET_Z + passThrough * (isDanger ? 1.05 : 0.18);

    group.position.set(check.laneX, check.height, z);
    group.scale.setScalar(0.68 + approach * 0.32 + impact * 0.16);

    const sliced = !isDanger && elapsed >= step.impact;
    if (bodyRef.current) bodyRef.current.visible = !sliced;
    if (splitRef.current) {
      splitRef.current.visible = sliced;
      const split = smoothStep(afterImpact / 0.42);
      splitRef.current.scale.setScalar(Math.max(0.18, 1 - split * 0.42));
      if (topHalfRef.current) {
        topHalfRef.current.position.set(
          vx * split * 0.22,
          vy * split * 0.22 + split * 0.2,
          split * 0.08,
        );
      }
      if (bottomHalfRef.current) {
        bottomHalfRef.current.position.set(
          -vx * split * 0.22,
          -vy * split * 0.22 - split * 0.2,
          split * 0.08,
        );
      }
    }

    if (slashRef.current) {
      slashRef.current.visible =
        !isDanger && Math.abs(elapsed - step.impact) < 0.18;
      slashRef.current.scale.setScalar(1 + impact * 0.38);
    }
  });

  return (
    <group
      ref={groupRef}
      visible={false}
      position={[check.laneX, check.height, DEMO_APPROACH_Z]}
    >
      <group ref={bodyRef}>
        <mesh scale={isDanger ? [1, 1.72, 0.16] : [1.52, 1.52, 1.52]}>
          {isDanger ? (
            <boxGeometry args={[bodySize, bodySize, bodySize]} />
          ) : (
            <sphereGeometry args={[bodySize * 0.5, 28, 18]} />
          )}
          <meshStandardMaterial
            color={color}
            emissive={core}
            emissiveIntensity={1.15}
            transparent
            opacity={isDanger ? 0.48 : 0.6}
            depthWrite={false}
          />
        </mesh>
        {!isDanger && (
          <mesh scale={[target.glowScale, target.glowScale, target.glowScale]}>
            <sphereGeometry args={[bodySize * 0.5, 24, 12]} />
            <meshBasicMaterial
              color={color}
              transparent
              opacity={0.16}
              depthWrite={false}
            />
          </mesh>
        )}
      </group>
      {!isDanger && (
        <group ref={splitRef} visible={false}>
          <mesh ref={topHalfRef}>
            <sphereGeometry
              args={[bodySize * 0.5, 18, 8, 0, Math.PI * 2, 0, Math.PI / 2]}
            />
            <meshStandardMaterial
              color={color}
              emissive={accent}
              emissiveIntensity={1.7}
              transparent
              opacity={0.72}
            />
          </mesh>
          <mesh ref={bottomHalfRef}>
            <sphereGeometry
              args={[
                bodySize * 0.5,
                18,
                8,
                0,
                Math.PI * 2,
                Math.PI / 2,
                Math.PI / 2,
              ]}
            />
            <meshStandardMaterial
              color={color}
              emissive={accent}
              emissiveIntensity={1.7}
              transparent
              opacity={0.72}
            />
          </mesh>
        </group>
      )}
      <mesh rotation={[0, 0, Math.PI / 2]}>
        <torusGeometry
          args={[isDanger ? bodySize * 0.7 : bodySize * 0.58, 0.009, 8, 48]}
        />
        <meshBasicMaterial
          color={isDanger ? accent : colors.gold}
          transparent
          opacity={0.84}
          depthWrite={false}
        />
      </mesh>
      {!isDanger && (
        <group ref={slashRef} visible={false} rotation={[0, 0, slashRotation]}>
          <mesh>
            <boxGeometry args={[0.028, bodySize * 2.1, 0.014]} />
            <meshBasicMaterial
              color={colors.gold}
              transparent
              opacity={0.78}
              depthWrite={false}
            />
          </mesh>
        </group>
      )}
      <Text
        {...TEXT_PROPS}
        position={[0, 0, 0.055]}
        fontSize={isDanger || check.dir === "any" ? 0.06 : 0.16}
        color={colors.washi}
        anchorX="center"
        anchorY="middle"
        textAlign="center"
      >
        {label}
      </Text>
      <Text
        {...TEXT_PROPS}
        position={[0, -0.34, 0.04]}
        fontSize={0.038}
        color={isDanger ? accent : shadow}
        anchorX="center"
        anchorY="middle"
        maxWidth={0.7}
        textAlign="center"
      >
        {check.label.toUpperCase()}
      </Text>
    </group>
  );
}

function TutorialDemoBodyGuide({
  steps,
  timeRef,
}: {
  steps: TutorialDemoStep[];
  timeRef: React.MutableRefObject<number>;
}) {
  const groupRef = useRef<Group | null>(null);

  useFrame(() => {
    const group = groupRef.current;
    if (!group) return;

    const elapsed = timeRef.current;
    const active = steps.find(
      (step) =>
        isDangerCheck(step.check) &&
        elapsed >= step.start - 0.2 &&
        elapsed <= step.end + 0.2,
    );

    if (!active) {
      group.visible = false;
      return;
    }

    group.visible = true;
    const kind = active.check.kind ?? "dodge-left";
    const [moveX, moveY] = movementForDanger(kind);
    const progress = smoothStep((elapsed - active.start) / 0.8);
    group.position.set(moveX * progress, 1.12 + moveY * progress, -0.6);
  });

  return (
    <group ref={groupRef} visible={false}>
      <mesh>
        <sphereGeometry args={[0.11, 18, 12]} />
        <meshBasicMaterial
          color={colors.washi}
          transparent
          opacity={0.2}
          depthWrite={false}
        />
      </mesh>
      <mesh position={[0, -0.28, 0]} scale={[0.18, 0.34, 0.05]}>
        <sphereGeometry args={[1, 18, 12]} />
        <meshBasicMaterial
          color={colors.washi}
          transparent
          opacity={0.14}
          depthWrite={false}
        />
      </mesh>
      <Text
        {...TEXT_PROPS}
        position={[0, -0.66, 0]}
        fontSize={0.042}
        color={colors.gold}
        anchorX="center"
        anchorY="middle"
        letterSpacing={0.06}
      >
        MOVE
      </Text>
    </group>
  );
}

export function TutorialDemo3D() {
  const phase = useGameStore((s) => s.phase);
  const activeTutorialPhase = useGameStore((s) => s.activeTutorialPhase);
  const finishTutorialDemo = useGameStore((s) => s.finishTutorialDemo);
  const startedAt = useRef<number | null>(null);
  const doneRef = useRef(false);
  const timeRef = useRef(0);
  const tutorial =
    activeTutorialPhase == null
      ? null
      : getTutorialByPhase(activeTutorialPhase);
  const steps = useMemo(
    () => (tutorial ? buildTutorialDemoSteps(tutorial) : []),
    [tutorial],
  );
  const ghostDemo = useMemo(
    () => (tutorial ? buildTutorialGhostDemo(tutorial, steps) : null),
    [tutorial, steps],
  );
  const duration = useMemo(
    () => ghostDemo?.loopSeconds ?? demoDurationForSteps(steps),
    [ghostDemo, steps],
  );

  useEffect(() => {
    if (phase !== "demo") return;
    startedAt.current = null;
    doneRef.current = false;
    timeRef.current = 0;
  }, [phase, activeTutorialPhase]);

  useFrame(({ clock }) => {
    if (phase !== "demo" || activeTutorialPhase == null) return;
    if (startedAt.current == null) startedAt.current = clock.elapsedTime;

    const elapsed = Math.max(0, clock.elapsedTime - startedAt.current);
    timeRef.current = elapsed;

    if (!doneRef.current && elapsed >= duration) {
      doneRef.current = true;
      finishTutorialDemo();
    }
  });

  if (!GHOST_DEMO_ENABLED || phase !== "demo" || !tutorial || !ghostDemo) {
    return null;
  }

  const phaseLabel =
    tutorial.phase === 0 ? "ONBOARDING" : `PHASE ${tutorial.phase}`;

  return (
    <>
      <group position={[0, 2.05, -1.3]}>
        <Text
          {...TEXT_PROPS}
          position={[0, 0, 0]}
          fontSize={0.052}
          color={colors.steel}
          anchorX="center"
          anchorY="middle"
          letterSpacing={0.08}
        >
          DEMO
        </Text>
        <Text
          {...TEXT_PROPS}
          position={[0, -0.12, 0]}
          fontSize={0.072}
          color={colors.amber}
          anchorX="center"
          anchorY="middle"
          maxWidth={1.8}
          textAlign="center"
        >
          {`${phaseLabel} - ${tutorial.title}`}
        </Text>
      </group>

      <GhostBlade demo={ghostDemo} scale={1.18} loop={false} />
      {steps.map((step) => (
        <TutorialDemoTarget
          key={`${tutorial.phase}-${step.index}`}
          step={step}
          tutorialPhase={tutorial.phase}
          timeRef={timeRef}
        />
      ))}
      <TutorialDemoBodyGuide steps={steps} timeRef={timeRef} />
    </>
  );
}

function SeijiPresence() {
  return (
    <group position={[-1.18, 0.94, -1.02]} rotation={[0, 0.18, 0]}>
      <mesh position={[0, 0.46, 0]}>
        <sphereGeometry args={[0.075, 16, 10]} />
        <meshStandardMaterial
          color="#1b1714"
          emissive="#30251a"
          emissiveIntensity={0.35}
        />
      </mesh>
      <mesh position={[0, 0.18, 0]} scale={[0.22, 0.38, 0.08]}>
        <sphereGeometry args={[1, 18, 12]} />
        <meshStandardMaterial
          color="#18130f"
          emissive="#3b2c1c"
          emissiveIntensity={0.22}
          transparent
          opacity={0.92}
        />
      </mesh>
      <mesh position={[0.02, -0.28, -0.02]} rotation={[0.18, 0, -0.15]}>
        <boxGeometry args={[0.045, 0.045, 0.8]} />
        <meshStandardMaterial color="#21170f" />
      </mesh>
      <Text
        {...TEXT_PROPS}
        position={[0, -0.58, 0]}
        fontSize={0.04}
        color={colors.steel}
        anchorX="center"
        anchorY="middle"
      >
        SEIJI
      </Text>
    </group>
  );
}

// The world-space lesson panel actually used by the game. Renders the "teach"
// phase (Seiji's lines, advanced with B/Y) as well as the dormant "consolidate"
// practice phase. This is what the installed PWA shows when a campaign phase's
// lesson runs — the DOM briefing card (TutorialBriefingScreen) never renders
// inside an immersive session, so without this the lesson was invisible in VR
// and the campaign appeared to hang on phase 1.
export function TeachLessonPanel3D() {
  const phase = useGameStore((s) => s.phase);
  const activeTutorialPhase = useGameStore((s) => s.activeTutorialPhase);
  const tutorialLineIndex = useGameStore((s) => s.tutorialLineIndex);
  const feedback = useGameStore((s) => s.tutorialFeedback);
  const attemptCount = useGameStore((s) => s.tutorialAttemptCount);

  // Computed defensively (tutorial may be undefined) so the prefetch effect
  // below can sit before any early return — hooks must run in the same
  // order every render, and the early return for "nothing to show" comes
  // after it, not before.
  const tutorial =
    activeTutorialPhase == null ? undefined : getTutorialByPhase(activeTutorialPhase);
  const lineCount = Math.max(1, tutorial?.seijiLines.length ?? 1);
  const safeLineIndex = Math.min(tutorialLineIndex, lineCount - 1);
  const nextScreen = tutorial?.onboardingScreens?.[safeLineIndex + 1];

  // Warms the next onboarding screen's illustration ahead of time so
  // paging forward (B/Y) feels instant instead of popping in.
  useEffect(() => {
    if (nextScreen?.vrImage) prefetchPanelImage(ACTOR_ASSETS[nextScreen.vrImage]);
  }, [nextScreen]);

  if ((phase !== "teach" && phase !== "consolidate") || !tutorial) {
    return null;
  }

  const isTeach = phase === "teach";
  const currentLine = tutorial.seijiLines[safeLineIndex] ?? "";
  const hasNextLine = isTeach && safeLineIndex < lineCount - 1;
  const segmentTitle = isTeach ? "TEACH" : "CONSOLIDATE";
  const guidance = isTeach
    ? tutorial.ghostDemo.description
    : tutorial.training.drill;
  const primaryHint = isTeach ? (hasNextLine ? "NEXT" : "PRACTICE") : "BEGIN";

  // Per-screen data — phase 0 only. tutorials.ts:296 builds seijiLines as
  // ONBOARDING_SCREENS.map(...), so safeLineIndex reaches into both arrays
  // 1:1 — no separate per-screen navigation state needed. Phases 1-10
  // never set onboardingScreens, so every value below falls back to
  // exactly the field this panel showed before this redesign; only the
  // primitives (ShojiBacking/PanelText) and tier styling are new.
  const screen = tutorial.onboardingScreens?.[safeLineIndex];
  const imageUrl = screen?.vrImage ? ACTOR_ASSETS[screen.vrImage] : null;

  const kickerText = screen
    ? screen.kicker
    : isTeach
      ? `${segmentTitle} ${safeLineIndex + 1}/${lineCount}`
      : segmentTitle;
  const titleText = screen
    ? screen.title
    : `${tutorial.phase === 0 ? "ONBOARDING" : `PHASE ${tutorial.phase}`} · ${tutorial.title}`;
  const kickerLabel = screen ? `${screen.kicker}    ${safeLineIndex + 1}/${lineCount}` : kickerText;
  const actionLabel = screen ? screen.primaryButton.toUpperCase() : primaryHint;

  return (
    <>
      {GHOST_DEMO_ENABLED && (
        <GhostBlade demo={tutorial.ghostDemo} position={[-0.94, 0, -1.08]} />
      )}

      <group position={[0.32, 1.58, -1.86]}>
        <PanelErrorBoundary label="TeachLessonPanel3D">
          <Suspense fallback={null}>
            <ShojiBacking width={1.62} height={1.42}>
              <PanelRule length={1.5} position={[0, 0.67, 0]} />

              <PanelText tier="kicker" position={[-0.68, 0.54, 0]} anchorX="left">
                {kickerLabel}
              </PanelText>
              <PanelText
                tier="title"
                position={[-0.68, 0.42, 0]}
                anchorX="left"
                fontSize={0.075}
                maxWidth={1.34}
              >
                {titleText}
              </PanelText>

              {/* Real measured stacking (PanelColumn), not fixed Y slots —
                  content length varies a lot per screen (the grade-tier
                  enumeration on "How the Dojo Judges" is ~4x the median),
                  and fixed slots were overlapping on the longer ones. Order
                  and fields for the rich (screen-driven) branch mirror the
                  approved DOM briefing exactly: Seiji's quote, then an
                  "Instruction" label + body, then any failureHint /
                  secondaryFailureHint / successFeedback — screen.ghostCaption
                  is intentionally not shown here; the DOM never renders it
                  either. key={safeLineIndex} forces a clean re-measure on
                  every screen change instead of interpolating between two
                  different texts' heights. */}
              <PanelColumn key={safeLineIndex} y={0.26} x={-0.68} gap={0.035}>
                {screen
                  ? [
                      <PanelText key="seiji" tier="seiji" anchorX="left" maxWidth={1.32}>
                        {screen.seiji.join("\n\n")}
                      </PanelText>,
                      <PanelText key="instr-label" tier="sectionLabel" anchorX="left">
                        Instruction
                      </PanelText>,
                      <PanelText key="instr-body" tier="instruction" anchorX="left" maxWidth={1.32}>
                        {screen.instruction}
                      </PanelText>,
                      screen.failureHint && (
                        <PanelText key="hint-fail" tier="hint" anchorX="left" maxWidth={1.32}>
                          {`CORRECTION   ${screen.failureHint}`}
                        </PanelText>
                      ),
                      screen.failureHint && screen.secondaryFailureHint && (
                        <PanelText key="hint-also" tier="hint" anchorX="left" maxWidth={1.32}>
                          {`ALSO   ${screen.secondaryFailureHint}`}
                        </PanelText>
                      ),
                      screen.failureHint && screen.successFeedback && (
                        <PanelText key="hint-success" tier="instruction" anchorX="left" maxWidth={1.32}>
                          {`WHEN TRUE   ${screen.successFeedback}`}
                        </PanelText>
                      ),
                    ]
                  : [
                      <PanelText key="element" tier="instruction" anchorX="left" maxWidth={1.32}>
                        {tutorial.element}
                      </PanelText>,
                      <PanelText key="guidance" tier="instruction" anchorX="left" maxWidth={1.32}>
                        {guidance}
                      </PanelText>,
                      <PanelText
                        key="detail"
                        tier={isTeach ? "seiji" : "feedbackWarn"}
                        anchorX="left"
                        maxWidth={1.32}
                      >
                        {isTeach ? currentLine : `Mistake: ${tutorial.beginnerError}`}
                      </PanelText>,
                      !isTeach && (
                        <PanelText
                          key="feedback"
                          tier={feedback?.ok ? "feedbackOk" : "feedbackWarn"}
                          anchorX="left"
                          maxWidth={1.32}
                        >
                          {feedback
                            ? `${feedback.headline}\n${feedback.detail}`
                            : `Cut the practice target. Suggested reps: ${tutorial.training.reps}.`}
                        </PanelText>
                      ),
                      !isTeach && (
                        <PanelText key="attempts" tier="secondary" anchorX="left">
                          {`Attempts ${attemptCount} · training never blocks`}
                        </PanelText>
                      ),
                    ]}
              </PanelColumn>

              <PanelText tier="footerHint" position={[0, -0.74, 0]}>
                {`STICK ← → · BACK / ${actionLabel}    B / Y · ${actionLabel}    A / X · SKIP    STICK CLICK · MENU`}
              </PanelText>
            </ShojiBacking>
          </Suspense>
        </PanelErrorBoundary>
      </group>

      {/* Second scroll, mirroring the DOM briefing's two-column layout —
          only for phase 0 screens that carry a vrImage. GhostBlade is off
          (GHOST_DEMO_ENABLED=false), so this space was already free. */}
      {imageUrl && (
        <group position={[-1.05, 1.58, -1.86]}>
          <PanelErrorBoundary label="TeachLessonPanel3D/illustration">
            <Suspense fallback={null}>
              <ShojiBacking width={0.95} height={1.42}>
                <PanelIllustration url={imageUrl} maxWidth={0.85} maxHeight={1.3} />
              </ShojiBacking>
            </Suspense>
          </PanelErrorBoundary>
        </group>
      )}

      <TutorialPracticeTarget tutorial={tutorial} />
    </>
  );
}

// Dormant "consolidate" practice panel (richer onboarding-screen content).
// Not mounted: with GHOST_DEMO_ENABLED off the tutorial flow is
// teach → countdown, so "consolidate" is never reached. Kept for when the
// practice-drill phase is reintroduced; TeachLessonPanel3D above is what runs.
export function ConsolidatePanel3D() {
  const phase = useGameStore((s) => s.phase);
  const activeTutorialPhase = useGameStore((s) => s.activeTutorialPhase);
  const feedback = useGameStore((s) => s.tutorialFeedback);
  const attemptCount = useGameStore((s) => s.tutorialAttemptCount);

  if (phase !== "consolidate" || activeTutorialPhase == null) {
    return null;
  }

  const tutorial = getTutorialByPhase(activeTutorialPhase);
  if (!tutorial) return null;

  const segmentTitle = "CONSOLIDATE";
  const onboardingPractice = tutorial.onboardingScreens?.at(-1);
  const guidance =
    onboardingPractice?.initialPracticeText ?? tutorial.training.drill;
  const primaryHint = tutorial.phase === 0 ? "ENTER THE DOJO" : "BEGIN";
  const displayTitle = onboardingPractice?.title ?? tutorial.title;
  const displayInstruction =
    onboardingPractice?.instruction ?? tutorial.element;
  const fallbackPrompt =
    onboardingPractice?.failureHint ??
    `Cut the practice target. Suggested reps: ${tutorial.training.reps}.`;

  return (
    <>
      <group position={[0, 0.78, -1.55]}>
        <mesh position={[0, 0.08, -0.035]} renderOrder={2100}>
          <planeGeometry args={[2.04, 0.92]} />
          <meshBasicMaterial
            color={colors.ink}
            transparent
            opacity={0.72}
            depthTest={false}
            depthWrite={false}
          />
        </mesh>
        <mesh position={[0, 0.56, -0.025]} renderOrder={2101}>
          <planeGeometry args={[2.04, 0.012]} />
          <meshBasicMaterial
            color={colors.amber}
            transparent
            opacity={0.7}
            depthTest={false}
            depthWrite={false}
          />
        </mesh>

        <Text
          {...TEXT_PROPS}
          position={[-0.9, 0.44, 0]}
          fontSize={0.04}
          color={colors.steel}
          anchorX="left"
          anchorY="middle"
          letterSpacing={0.08}
        >
          {segmentTitle}
        </Text>
        <Text
          {...TEXT_PROPS}
          position={[-0.9, 0.31, 0]}
          fontSize={0.064}
          color={colors.amber}
          anchorX="left"
          anchorY="middle"
          maxWidth={1.76}
        >
          {`${tutorial.phase === 0 ? "ONBOARDING" : `PHASE ${tutorial.phase}`} - ${displayTitle}`}
        </Text>
        <Text
          {...TEXT_PROPS}
          position={[-0.9, 0.16, 0]}
          fontSize={0.036}
          color={colors.washi}
          anchorX="left"
          anchorY="top"
          maxWidth={1.76}
          lineHeight={1.25}
        >
          {displayInstruction}
        </Text>

        <Text
          {...TEXT_PROPS}
          position={[-0.9, -0.02, 0]}
          fontSize={0.04}
          color={colors.washi}
          anchorX="left"
          anchorY="top"
          maxWidth={1.76}
          lineHeight={1.25}
        >
          {guidance}
        </Text>

        <Text
          {...TEXT_PROPS}
          position={[-0.9, -0.31, 0]}
          fontSize={0.04}
          color={feedback?.ok ? colors.gold : colors.amber}
          anchorX="left"
          anchorY="top"
          maxWidth={1.76}
          lineHeight={1.25}
        >
          {feedback
            ? `${feedback.headline}\n${feedback.detail}`
            : fallbackPrompt}
        </Text>
        <Text
          {...TEXT_PROPS}
          position={[-0.9, -0.5, 0]}
          fontSize={0.033}
          color={colors.border}
          anchorX="left"
          anchorY="middle"
          maxWidth={1.76}
        >
          {`Attempts ${attemptCount} - training never blocks`}
        </Text>

        <Text
          {...TEXT_PROPS}
          position={[0, -0.42, 0]}
          fontSize={0.032}
          color={colors.border}
          anchorX="center"
          anchorY="middle"
          letterSpacing={0.08}
        >
          {`ENTER / B / Y - ${primaryHint}    S / A / X - SKIP    LEFT STICK - MENU`}
        </Text>
      </group>

      <TutorialPracticeTarget tutorial={tutorial} />
    </>
  );
}

function isInteractiveKeyTarget(target: EventTarget | null): boolean {
  return (
    target instanceof HTMLElement &&
    ["A", "BUTTON", "INPUT", "SELECT", "TEXTAREA"].includes(target.tagName)
  );
}

export function TutorialBriefingScreen({
  onEnterDemo,
}: {
  onEnterDemo?: () => void;
}) {
  const phase = useGameStore((s) => s.phase);
  const activeTutorialPhase = useGameStore((s) => s.activeTutorialPhase);
  const tutorialLineIndex = useGameStore((s) => s.tutorialLineIndex);
  const advanceTutorial = useGameStore((s) => s.advanceTutorial);
  const backTutorial = useGameStore((s) => s.backTutorial);
  const skipTutorial = useGameStore((s) => s.skipTutorial);
  const tutorial =
    activeTutorialPhase == null
      ? null
      : getTutorialByPhase(activeTutorialPhase);
  const onboardingScreens = tutorial?.onboardingScreens;
  const lineCount = Math.max(
    1,
    onboardingScreens?.length ?? tutorial?.seijiLines.length ?? 1,
  );
  const safeLineIndex = Math.min(tutorialLineIndex, lineCount - 1);
  const hasNextLine = safeLineIndex < lineCount - 1;
  const hasPreviousLine = safeLineIndex > 0;

  // Only enter VR once the tutorial chain actually lands on a playable phase.
  // `teach` states are DOM-only (this briefing card); the onboarding →
  // phase-1 chain stays in `teach`, so entering VR on the last onboarding
  // screen strands the player inside immersion with the lesson card still
  // active but invisible (it's DOM, and TeachPanel3D only renders in the
  // never-reached `consolidate` phase). Defer VR entry until after the advance
  // resolves, mirroring Menu's existing `phase !== "teach"` guard.
  const handleNext = useCallback(() => {
    unlockRegisteredTracks();
    const wasLastLine = !hasNextLine;
    advanceTutorial();
    if (wasLastLine && useGameStore.getState().phase !== "teach") {
      onEnterDemo?.();
    }
  }, [advanceTutorial, hasNextLine, onEnterDemo]);

  const handleSkip = useCallback(() => {
    unlockRegisteredTracks();
    skipTutorial();
    if (useGameStore.getState().phase !== "teach") {
      onEnterDemo?.();
    }
  }, [onEnterDemo, skipTutorial]);

  const handleBack = useCallback(() => {
    if (!hasPreviousLine) return;
    backTutorial();
  }, [backTutorial, hasPreviousLine]);

  useEffect(() => {
    if (phase !== "teach") return;
    const onKey = (e: KeyboardEvent) => {
      if (isInteractiveKeyTarget(e.target)) return;
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        handleNext();
      }
      if (e.key === "s" || e.key === "S" || e.key === "Escape") {
        e.preventDefault();
        handleSkip();
      }
      if (e.key === "Backspace" || e.key === "ArrowLeft") {
        e.preventDefault();
        handleBack();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [phase, handleNext, handleSkip, handleBack]);

  if (phase !== "teach" || activeTutorialPhase == null) return null;
  if (!tutorial) return null;

  const progressPct = ((safeLineIndex + 1) / lineCount) * 100;
  const phaseLabel =
    tutorial.phase === 0 ? "ONBOARDING" : `PHASE ${tutorial.phase}`;
  const onboardingScreen = onboardingScreens?.[safeLineIndex];
  const displayKicker = onboardingScreen?.kicker ?? phaseLabel;
  const displayTitle = onboardingScreen?.title ?? tutorial.title;
  const seijiLines = onboardingScreen?.seiji ?? [
    tutorial.seijiLines[safeLineIndex] ?? "",
  ];
  const instruction = onboardingScreen?.instruction ?? tutorial.element;
  const primaryLabel = hasNextLine
    ? "Next"
    : (onboardingScreen?.primaryButton ?? "Continue");

  if (tutorial.phase !== 0) {
    const lessonLine = seijiLines[0] ?? "";
    const lessonImage = getLessonStagingImage(tutorial.phase);
    const lessonTag = `LESSON ${String(tutorial.phase).padStart(2, "0")} / 10`;

    return (
      <div style={lessonBeatScreen}>
        <div style={lessonBeatShell}>
          <section style={lessonBeatFrame}>
            <img
              src={lessonImage}
              alt={`${displayTitle} lesson staging`}
              style={lessonBeatImage}
            />
            <div style={lessonBeatVignette} />
            <div style={lessonBeatTag}>
              <span style={lessonBeatKanji}>間</span>
              <span>{lessonTag}</span>
            </div>
            <div style={lessonBeatDialoguePlate}>
              <div style={lessonBeatSpeakerRow}>
                <span style={lessonBeatSpeakerChip} />
                <span style={lessonBeatSpeakerKanji}>静二</span>
                <span style={lessonBeatSpeakerName}>SEIJI</span>
              </div>
              <p style={lessonBeatLine}>{`"${lessonLine}"`}</p>
              <div style={lessonBeatFooter}>
                <div style={lessonBeatProgressGroup}>
                  <span>{`${safeLineIndex + 1}/${lineCount}`}</span>
                  <span style={lessonBeatProgressTrack}>
                    <span
                      style={{
                        ...lessonBeatProgressFill,
                        width: `${progressPct}%`,
                      }}
                    />
                  </span>
                </div>
                <button onClick={handleNext} style={lessonBeatInlineCta}>
                  {primaryLabel}
                  <span style={lessonBeatCaret}>▸</span>
                </button>
              </div>
            </div>
          </section>

          <aside style={lessonBeatCopyPanel}>
            <div style={lessonBeatMeta}>
              <span>{displayKicker}</span>
              <span>{lessonTag}</span>
            </div>
            <h1 style={lessonBeatTitle}>{displayTitle}</h1>
            <p style={lessonBeatElement}>{tutorial.element}</p>
            <div style={lessonInstructionPanel}>
              <span style={lessonInstructionKicker}>Instruction</span>
              <p style={lessonInstructionText}>{instruction}</p>
            </div>
            <div style={lessonInstructionPanel}>
              <span style={lessonInstructionKicker}>Drill</span>
              <p style={lessonInstructionText}>{tutorial.training.drill}</p>
            </div>
            <p style={lessonCorrectionText}>{tutorial.correction}</p>
            <div style={lessonBeatSpacer} />
            <div style={briefingActions}>
              <button
                onClick={handleBack}
                disabled={!hasPreviousLine}
                style={
                  hasPreviousLine
                    ? briefingSecondaryButton
                    : briefingDisabledButton
                }
              >
                Back
              </button>
              <button onClick={handleSkip} style={briefingSecondaryButton}>
                Skip
              </button>
              <button onClick={handleNext} style={briefingPrimaryButton}>
                {primaryLabel}
              </button>
            </div>
          </aside>
        </div>
      </div>
    );
  }

  const isDoorOpensScreen =
    tutorial.phase === 0 && onboardingScreen?.title === "The Door Opens";
  const isNoSparringScreen =
    tutorial.phase === 0 && onboardingScreen?.title === "No Sparring";
  const isKatanaGripScreen =
    tutorial.phase === 0 && onboardingScreen?.title === "One Katana";
  const isLikeItMattersScreen =
    tutorial.phase === 0 && onboardingScreen?.title === "Like It Matters";
  const isChudanScreen =
    tutorial.phase === 0 && onboardingScreen?.title === "Chudan";
  const isHonestCutScreen =
    tutorial.phase === 0 && onboardingScreen?.title === "An Honest Cut";
  const isFirstCutScreen =
    tutorial.phase === 0 && onboardingScreen?.title === "The First Cut";
  const isDoNotClashScreen =
    tutorial.phase === 0 && onboardingScreen?.title === "Do Not Clash";
  const isHowTheDojoJudgesScreen =
    tutorial.phase === 0 && onboardingScreen?.title === "How the Dojo Judges";
  const isTheBladeRemembersScreen =
    tutorial.phase === 0 && onboardingScreen?.title === "The Blade Remembers";
  const isThreeWordsScreen =
    tutorial.phase === 0 && onboardingScreen?.title === "Three Words";
  const isFirstRepetitionScreen =
    tutorial.phase === 0 && onboardingScreen?.title === "First Repetition";

  return (
    <div style={briefingScreen}>
      <div style={briefingShell}>
        <section style={briefingVisualPanel}>
          <div style={briefingSceneFrame}>
            <div style={moonDisc} />
            <div style={dojoFloorGlow} />
            {isDoorOpensScreen ? (
              <>
                <img
                  src={ACTOR_ASSETS.theDoorOpens}
                  alt="Masaru standing at the dojo door"
                  style={masaruFullFrameImage}
                />
                <div style={singleActorNameplate}>MASARU</div>
              </>
            ) : isNoSparringScreen ? (
              <>
                <img
                  src={ACTOR_ASSETS.noSparring}
                  alt="Seiji refusing the sparring match"
                  style={seijiFullFrameImage}
                />
                <div style={singleActorNameplate}>SEIJI</div>
              </>
            ) : isKatanaGripScreen ? (
              <img
                src={ACTOR_ASSETS.oneKatana}
                alt="Both hands holding one controller as a katana"
                style={katanaGripImage}
              />
            ) : isLikeItMattersScreen ? (
              <img
                src={ACTOR_ASSETS.likeItMatters}
                alt="Samurai holding one controller with both hands in the dojo"
                style={cyberDojoGripImage}
              />
            ) : isChudanScreen ? (
              <img
                src={ACTOR_ASSETS.chudanHonestCut}
                alt="Masaru in chudan guard with one blade"
                style={chudanKamaeImage}
              />
            ) : isHonestCutScreen ? (
              <img
                src={ACTOR_ASSETS.honestCutTriptych}
                alt="Masaru demonstrating an honest cut in three panels"
                style={honestCutTriptychImage}
              />
            ) : isFirstCutScreen ? (
              <img
                src={ACTOR_ASSETS.theFirstCut}
                alt="Masaru cutting through the first practice target"
                style={firstCutImage}
              />
            ) : isDoNotClashScreen ? (
              <img
                src={ACTOR_ASSETS.doNotClash}
                alt="Masaru dodging a red obstacle while holding the katana"
                style={doNotClashImage}
              />
            ) : isHowTheDojoJudgesScreen ? (
              <img
                src={ACTOR_ASSETS.howTheDojoJudges}
                alt="Dojo judgment marks ordered from miss to ippon"
                style={howTheDojoJudgesImage}
              />
            ) : isTheBladeRemembersScreen ? (
              <img
                src={ACTOR_ASSETS.theBladeRemembers}
                alt="A master cut waking the blade into gold"
                style={theBladeRemembersImage}
              />
            ) : isThreeWordsScreen ? (
              <img
                src={ACTOR_ASSETS.threeWords}
                alt="Kamae, tenouchi, and zanshin shown as three sword moments"
                style={threeWordsImage}
              />
            ) : isFirstRepetitionScreen ? (
              <img
                src={ACTOR_ASSETS.firstRepetition}
                alt="Masaru ready for his first repetition with both hands on the controller"
                style={firstRepetitionImage}
              />
            ) : (
              <>
                <img
                  src={ACTOR_ASSETS.noSparring}
                  alt="Seiji standing in the dojo"
                  style={seijiActorImage}
                />
                <img
                  src={ACTOR_ASSETS.theDoorOpens}
                  alt="Masaru facing his first lesson"
                  style={masaruActorImage}
                />
                <div style={actorNameplate}>
                  <span>SEIJI</span>
                  <span>MASARU</span>
                </div>
              </>
            )}
          </div>
        </section>

        <section style={briefingCopyPanel}>
          <div style={briefingMeta}>
            <span>{displayKicker}</span>
            <span>{`${safeLineIndex + 1}/${lineCount}`}</span>
          </div>
          <h1 style={briefingTitle}>{displayTitle}</h1>
          <div style={briefingDialogueHeader}>
            <img
              src={ACTOR_ASSETS.seijiPortrait}
              alt=""
              style={briefingPortrait}
            />
            <span>Seiji</span>
          </div>
          <div style={briefingDivider} />
          <div style={briefingLineGroup}>
            {seijiLines.map((line) => (
              <p key={line} style={briefingLine}>
                {line}
              </p>
            ))}
          </div>
          <div style={instructionPanel}>
            <span style={instructionKicker}>Instruction</span>
            <p style={instructionText}>{instruction}</p>
          </div>
          {onboardingScreen?.failureHint && (
            <div style={hintGroup}>
              <p style={hintText}>
                <span style={hintLabel}>Correction</span>
                {onboardingScreen.failureHint}
              </p>
              {onboardingScreen.secondaryFailureHint && (
                <p style={hintText}>
                  <span style={hintLabel}>Also</span>
                  {onboardingScreen.secondaryFailureHint}
                </p>
              )}
              {onboardingScreen.successFeedback && (
                <p style={successHintText}>
                  <span style={hintLabel}>When true</span>
                  {onboardingScreen.successFeedback}
                </p>
              )}
            </div>
          )}
          <div style={progressTrack}>
            <div style={{ ...progressFill, width: `${progressPct}%` }} />
          </div>
          <div style={briefingActions}>
            <button
              onClick={handleBack}
              disabled={!hasPreviousLine}
              style={
                hasPreviousLine
                  ? briefingSecondaryButton
                  : briefingDisabledButton
              }
            >
              Back
            </button>
            <button onClick={handleSkip} style={briefingSecondaryButton}>
              Skip
            </button>
            <button onClick={handleNext} style={briefingPrimaryButton}>
              {primaryLabel}
            </button>
          </div>
        </section>
      </div>
    </div>
  );
}

export function TutorialOverlay() {
  const phase = useGameStore((s) => s.phase);
  const activeTutorialPhase = useGameStore((s) => s.activeTutorialPhase);
  const tutorialLineIndex = useGameStore((s) => s.tutorialLineIndex);
  const advanceTutorial = useGameStore((s) => s.advanceTutorial);
  const skipTutorial = useGameStore((s) => s.skipTutorial);
  const clearTutorialFeedback = useGameStore((s) => s.clearTutorialFeedback);
  const handleAdvanceTutorial = useCallback(() => {
    unlockRegisteredTracks();
    advanceTutorial();
  }, [advanceTutorial]);
  const handleSkipTutorial = useCallback(() => {
    unlockRegisteredTracks();
    skipTutorial();
  }, [skipTutorial]);

  useEffect(() => {
    if (phase !== "consolidate") return;
    const onKey = (e: KeyboardEvent) => {
      if (isInteractiveKeyTarget(e.target)) return;
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        handleAdvanceTutorial();
      }
      if (e.key === "s" || e.key === "S") {
        e.preventDefault();
        handleSkipTutorial();
      }
      if (e.key === "r" || e.key === "R") {
        e.preventDefault();
        clearTutorialFeedback();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [phase, handleAdvanceTutorial, handleSkipTutorial, clearTutorialFeedback]);

  if (phase !== "consolidate" || activeTutorialPhase == null) {
    return null;
  }

  const tutorial = getTutorialByPhase(activeTutorialPhase);
  const primaryLabel =
    tutorial?.phase === 0 ? "Enter the Dojo" : "Begin Challenge";

  return (
    <div style={overlayActions}>
      <button onClick={handleSkipTutorial} style={secondaryAction}>
        Skip
      </button>
      {phase === "consolidate" && (
        <button onClick={clearTutorialFeedback} style={secondaryAction}>
          Practice Again
        </button>
      )}
      <button onClick={handleAdvanceTutorial} style={primaryAction}>
        {primaryLabel}
      </button>
    </div>
  );
}

const overlayActions: React.CSSProperties = {
  position: "fixed",
  bottom: 40,
  left: "50%",
  transform: "translateX(-50%)",
  display: "flex",
  gap: 16,
};

const primaryAction: React.CSSProperties = {
  ...primaryButton,
  ...typography.ui,
  padding: "14px 34px",
  fontSize: 16,
};

const secondaryAction: React.CSSProperties = {
  ...secondaryButton,
  ...typography.ui,
  padding: "14px 30px",
  fontSize: 16,
};

const lessonBeatScreen: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  zIndex: 10000,
  pointerEvents: "auto",
  color: colors.washi,
  background:
    "radial-gradient(110% 90% at 44% 112%, #241a10 0%, #181109 52%, #100c0a 100%)",
  ...typography.ui,
  display: "grid",
  placeItems: "center",
  padding: 32,
};

const lessonBeatShell: React.CSSProperties = {
  width: "min(1280px, 100%)",
  minHeight: "min(720px, calc(100vh - 64px))",
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 520px), 1fr))",
  gap: 28,
  alignItems: "stretch",
};

const lessonBeatFrame: React.CSSProperties = {
  position: "relative",
  overflow: "hidden",
  alignSelf: "center",
  aspectRatio: "16 / 9",
  border: `1px solid ${colors.border}`,
  borderRadius: 12,
  background: "#0b0705",
  boxShadow: "0 40px 90px -40px rgba(0, 0, 0, 0.95)",
};

const lessonBeatImage: React.CSSProperties = {
  position: "absolute",
  inset: 0,
  width: "100%",
  height: "100%",
  objectFit: "cover",
  objectPosition: "50% 50%",
  filter: "saturate(0.94) contrast(1.04)",
};

const lessonBeatVignette: React.CSSProperties = {
  position: "absolute",
  inset: 0,
  background:
    "radial-gradient(70% 60% at 50% 44%, rgba(0,0,0,0) 0%, rgba(0,0,0,0) 62%, rgba(0,0,0,0.72) 100%)",
};

const lessonBeatTag: React.CSSProperties = {
  position: "absolute",
  left: 22,
  top: 20,
  display: "flex",
  alignItems: "center",
  gap: 10,
  color: "rgba(236, 227, 208, 0.74)",
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: "0.26em",
  textTransform: "uppercase",
};

const lessonBeatKanji: React.CSSProperties = {
  ...typography.display,
  color: colors.amber,
  fontSize: 18,
  letterSpacing: "0.04em",
};

const lessonBeatDialoguePlate: React.CSSProperties = {
  position: "absolute",
  left: 0,
  right: 0,
  bottom: 0,
  padding: "64px 46px 34px",
  background:
    "linear-gradient(180deg, rgba(11,7,5,0), rgba(11,7,5,0.82) 34%, rgba(11,7,5,0.95))",
};

const lessonBeatSpeakerRow: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 12,
  marginBottom: 12,
};

const lessonBeatSpeakerChip: React.CSSProperties = {
  width: 30,
  height: 30,
  borderRadius: "50%",
  flexShrink: 0,
  border: `1px solid rgba(224, 169, 80, 0.6)`,
  background: "radial-gradient(circle at 40% 35%, #c8c8ce, #5a5a60)",
};

const lessonBeatSpeakerKanji: React.CSSProperties = {
  ...typography.display,
  color: colors.amber,
  fontSize: 16,
  fontWeight: 700,
};

const lessonBeatSpeakerName: React.CSSProperties = {
  color: "rgba(224, 169, 80, 0.78)",
  fontSize: 10,
  fontWeight: 800,
  letterSpacing: "0.3em",
  paddingTop: 2,
};

const lessonBeatLine: React.CSSProperties = {
  ...typography.display,
  margin: 0,
  maxWidth: 820,
  color: "#f4e6c8",
  fontSize: "clamp(24px, 2.4vw, 34px)",
  fontStyle: "italic",
  lineHeight: 1.42,
};

const lessonBeatFooter: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 22,
  marginTop: 22,
};

const lessonBeatProgressGroup: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  color: "rgba(236, 227, 208, 0.5)",
  fontSize: 11,
  letterSpacing: "0.14em",
};

const lessonBeatProgressTrack: React.CSSProperties = {
  position: "relative",
  display: "block",
  width: 120,
  height: 2,
  background: "rgba(107, 78, 50, 0.5)",
};

const lessonBeatProgressFill: React.CSSProperties = {
  position: "absolute",
  inset: "0 auto 0 0",
  display: "block",
  height: "100%",
  background: colors.amber,
};

const lessonBeatInlineCta: React.CSSProperties = {
  border: 0,
  background: "transparent",
  color: "rgba(236, 227, 208, 0.74)",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  gap: 10,
  fontSize: 11,
  fontWeight: 800,
  letterSpacing: "0.22em",
  textTransform: "uppercase",
};

const lessonBeatCaret: React.CSSProperties = {
  color: colors.amber,
  fontSize: 15,
};

const lessonBeatCopyPanel: React.CSSProperties = {
  border: `1px solid ${colors.border}`,
  borderRadius: 12,
  background: "rgba(20, 15, 10, 0.58)",
  boxShadow: "0 40px 90px -46px rgba(0, 0, 0, 0.95)",
  padding: "30px 28px",
  display: "flex",
  flexDirection: "column",
};

const lessonBeatMeta: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  gap: 18,
  color: "rgba(236, 227, 208, 0.62)",
  fontSize: 11,
  fontWeight: 800,
  letterSpacing: "0.18em",
  textTransform: "uppercase",
};

const lessonBeatTitle: React.CSSProperties = {
  ...typography.display,
  margin: "24px 0 8px",
  color: colors.amber,
  fontSize: "clamp(42px, 4.7vw, 72px)",
  lineHeight: 1,
};

const lessonBeatElement: React.CSSProperties = {
  margin: "0 0 22px",
  color: "rgba(236, 227, 208, 0.72)",
  fontSize: 14,
  lineHeight: 1.55,
};

const lessonInstructionPanel: React.CSSProperties = {
  padding: "16px 17px",
  border: `1px solid rgba(224, 169, 80, 0.34)`,
  borderRadius: 8,
  background: "rgba(11, 7, 5, 0.32)",
  marginTop: 12,
};

const lessonInstructionKicker: React.CSSProperties = {
  display: "block",
  color: colors.steel,
  fontSize: 11,
  fontWeight: 900,
  letterSpacing: "0.2em",
  textTransform: "uppercase",
  marginBottom: 8,
};

const lessonInstructionText: React.CSSProperties = {
  margin: 0,
  color: colors.steel,
  fontSize: 15,
  lineHeight: 1.45,
};

const lessonCorrectionText: React.CSSProperties = {
  margin: "16px 0 0",
  color: "rgba(224, 169, 80, 0.86)",
  fontSize: 14,
  lineHeight: 1.45,
};

const lessonBeatSpacer: React.CSSProperties = {
  flex: 1,
};

const briefingScreen: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  zIndex: 10000,
  pointerEvents: "auto",
  color: colors.washi,
  background: `radial-gradient(110% 90% at 50% 0%, #352719 0%, #1d1510 48%, ${colors.ink} 100%)`,
  ...typography.ui,
  display: "grid",
  placeItems: "center",
  padding: 32,
  overflow: "hidden",
};

const briefingShell: React.CSSProperties = {
  width: "min(1180px, 100%)",
  height: "min(680px, calc(100vh - 64px))",
  minHeight: 0,
  display: "grid",
  gridTemplateColumns: "minmax(320px, 0.95fr) minmax(420px, 1.05fr)",
  gap: 24,
};

const briefingVisualPanel: React.CSSProperties = {
  display: "grid",
  gridTemplateRows: "1fr auto",
  gap: 16,
  minHeight: 0,
};

const briefingSceneFrame: React.CSSProperties = {
  position: "relative",
  overflow: "hidden",
  minHeight: 0,
  border: `1px solid ${colors.border}`,
  borderRadius: 8,
  background:
    "linear-gradient(180deg, rgba(17, 13, 10, 0.1), rgba(20, 16, 14, 0.9)), linear-gradient(120deg, #221812, #332415 56%, #15110f)",
  boxShadow: "0 28px 80px rgba(0, 0, 0, 0.45)",
};

const moonDisc: React.CSSProperties = {
  position: "absolute",
  top: 42,
  right: 48,
  width: 86,
  height: 86,
  borderRadius: "50%",
  background: "rgba(236, 227, 208, 0.18)",
  boxShadow: "0 0 70px rgba(236, 227, 208, 0.28)",
};

const dojoFloorGlow: React.CSSProperties = {
  position: "absolute",
  left: 0,
  right: 0,
  bottom: 0,
  height: "38%",
  background:
    "radial-gradient(70% 60% at 52% 94%, rgba(224, 169, 80, 0.34), rgba(20, 16, 14, 0.02) 72%)",
  zIndex: 0,
};

const seijiActorImage: React.CSSProperties = {
  position: "absolute",
  left: "4%",
  bottom: -8,
  height: "92%",
  maxWidth: "48%",
  objectFit: "contain",
  objectPosition: "bottom center",
  filter: "drop-shadow(0 18px 34px rgba(0, 0, 0, 0.72))",
  zIndex: 2,
};

const masaruActorImage: React.CSSProperties = {
  position: "absolute",
  right: "-2%",
  bottom: -22,
  height: "86%",
  maxWidth: "62%",
  objectFit: "contain",
  objectPosition: "bottom center",
  opacity: 0.88,
  filter:
    "saturate(0.9) contrast(1.05) drop-shadow(0 18px 42px rgba(0, 0, 0, 0.74))",
  zIndex: 1,
};

const masaruFullFrameImage: React.CSSProperties = {
  position: "absolute",
  inset: 0,
  width: "100%",
  height: "100%",
  objectFit: "cover",
  objectPosition: "50% 50%",
  filter: "saturate(0.92) contrast(1.04)",
  zIndex: 2,
};

const seijiFullFrameImage: React.CSSProperties = {
  position: "absolute",
  inset: 0,
  width: "100%",
  height: "100%",
  objectFit: "cover",
  objectPosition: "50% 0%",
  filter: "saturate(0.92) contrast(1.04)",
  zIndex: 2,
};

const katanaGripImage: React.CSSProperties = {
  position: "absolute",
  inset: 0,
  width: "100%",
  height: "100%",
  objectFit: "cover",
  objectPosition: "52% 50%",
  filter: "saturate(0.96) contrast(1.04)",
  zIndex: 2,
};

const cyberDojoGripImage: React.CSSProperties = {
  position: "absolute",
  inset: 0,
  width: "100%",
  height: "100%",
  objectFit: "cover",
  objectPosition: "50% 42%",
  filter: "saturate(0.94) contrast(1.04)",
  zIndex: 2,
};

const chudanKamaeImage: React.CSSProperties = {
  position: "absolute",
  inset: 0,
  width: "100%",
  height: "100%",
  objectFit: "cover",
  objectPosition: "50% 46%",
  filter: "saturate(0.94) contrast(1.04)",
  zIndex: 2,
};

const honestCutTriptychImage: React.CSSProperties = {
  position: "absolute",
  inset: 0,
  width: "100%",
  height: "100%",
  objectFit: "contain",
  objectPosition: "50% 50%",
  filter: "saturate(0.94) contrast(1.04)",
  zIndex: 2,
};

const firstCutImage: React.CSSProperties = {
  position: "absolute",
  inset: 0,
  width: "100%",
  height: "100%",
  objectFit: "cover",
  objectPosition: "54% 48%",
  filter: "saturate(0.96) contrast(1.04)",
  zIndex: 2,
};

const doNotClashImage: React.CSSProperties = {
  position: "absolute",
  inset: 0,
  width: "100%",
  height: "100%",
  objectFit: "cover",
  objectPosition: "52% 48%",
  filter: "saturate(0.96) contrast(1.04)",
  zIndex: 2,
};

const howTheDojoJudgesImage: React.CSSProperties = {
  position: "absolute",
  inset: 0,
  width: "100%",
  height: "100%",
  objectFit: "contain",
  objectPosition: "50% 50%",
  filter: "saturate(0.98) contrast(1.04)",
  zIndex: 2,
};

const theBladeRemembersImage: React.CSSProperties = {
  position: "absolute",
  inset: 0,
  width: "100%",
  height: "100%",
  objectFit: "cover",
  objectPosition: "50% 48%",
  filter: "saturate(0.96) contrast(1.04)",
  zIndex: 2,
};

const threeWordsImage: React.CSSProperties = {
  position: "absolute",
  inset: 0,
  width: "100%",
  height: "100%",
  objectFit: "contain",
  objectPosition: "50% 50%",
  filter: "saturate(0.94) contrast(1.04)",
  zIndex: 2,
};

const firstRepetitionImage: React.CSSProperties = {
  position: "absolute",
  inset: 0,
  width: "100%",
  height: "100%",
  objectFit: "cover",
  objectPosition: "50% 42%",
  filter: "saturate(0.94) contrast(1.04)",
  zIndex: 2,
};

const actorNameplate: React.CSSProperties = {
  position: "absolute",
  left: 26,
  right: 26,
  bottom: 18,
  display: "flex",
  justifyContent: "space-between",
  color: "rgba(236, 227, 208, 0.68)",
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: "0.16em",
  textTransform: "uppercase",
  zIndex: 3,
};

const singleActorNameplate: React.CSSProperties = {
  position: "absolute",
  left: 26,
  bottom: 18,
  color: "rgba(236, 227, 208, 0.74)",
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: "0.16em",
  textTransform: "uppercase",
  zIndex: 3,
};

const briefingCopyPanel: React.CSSProperties = {
  border: `1px solid ${colors.border}`,
  borderRadius: 8,
  background: "rgba(42, 31, 23, 0.72)",
  boxShadow: "0 28px 80px rgba(0, 0, 0, 0.45)",
  padding: "34px 40px",
  display: "flex",
  flexDirection: "column",
  minHeight: 0,
  overflowY: "auto",
  scrollbarWidth: "thin",
};

const briefingMeta: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  gap: 24,
  color: colors.steel,
  fontSize: 13,
  textTransform: "uppercase",
};

const briefingTitle: React.CSSProperties = {
  ...typography.display,
  margin: "24px 0 18px",
  color: colors.amber,
  fontSize: "clamp(38px, 5.4vw, 72px)",
  lineHeight: 1,
};

const briefingElement: React.CSSProperties = {
  margin: 0,
  color: colors.steel,
  fontSize: 16,
  lineHeight: 1.45,
};

const briefingDivider: React.CSSProperties = {
  width: 72,
  height: 2,
  background: colors.border,
  margin: "20px 0 24px",
};

const briefingDialogueHeader: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 12,
  color: colors.steel,
  fontSize: 13,
  fontWeight: 700,
  letterSpacing: "0.14em",
  textTransform: "uppercase",
};

const briefingPortrait: React.CSSProperties = {
  width: 52,
  height: 52,
  borderRadius: 6,
  objectFit: "cover",
  objectPosition: "52% 30%",
  border: `1px solid ${colors.border}`,
  boxShadow: "0 10px 28px rgba(0, 0, 0, 0.34)",
};

const briefingLineGroup: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 14,
  flex: 1,
};

const briefingLine: React.CSSProperties = {
  margin: 0,
  color: colors.washi,
  fontSize: "clamp(20px, 2.2vw, 31px)",
  lineHeight: 1.28,
};

const instructionPanel: React.CSSProperties = {
  marginTop: 26,
  padding: "16px 18px",
  border: `1px solid rgba(224, 169, 80, 0.34)`,
  borderRadius: 8,
  background: "rgba(20, 16, 14, 0.34)",
};

const instructionKicker: React.CSSProperties = {
  display: "block",
  color: colors.amber,
  fontSize: 12,
  fontWeight: 800,
  letterSpacing: "0.14em",
  textTransform: "uppercase",
  marginBottom: 8,
};

const instructionText: React.CSSProperties = {
  margin: 0,
  color: colors.steel,
  fontSize: 16,
  lineHeight: 1.45,
};

const hintGroup: React.CSSProperties = {
  display: "grid",
  gap: 8,
  marginTop: 12,
};

const hintText: React.CSSProperties = {
  margin: 0,
  color: colors.amber,
  fontSize: 14,
  lineHeight: 1.35,
};

const successHintText: React.CSSProperties = {
  ...hintText,
  color: colors.steel,
};

const hintLabel: React.CSSProperties = {
  display: "inline-block",
  minWidth: 86,
  color: "rgba(236, 227, 208, 0.58)",
  fontSize: 11,
  fontWeight: 800,
  letterSpacing: "0.12em",
  textTransform: "uppercase",
};

const progressTrack: React.CSSProperties = {
  height: 4,
  background: "rgba(107, 78, 50, 0.48)",
  borderRadius: 999,
  overflow: "hidden",
  marginTop: 36,
};

const progressFill: React.CSSProperties = {
  height: "100%",
  background: colors.amber,
};

const briefingActions: React.CSSProperties = {
  position: "sticky",
  bottom: -34,
  display: "flex",
  justifyContent: "flex-end",
  gap: 14,
  margin: "26px -40px -34px",
  padding: "18px 40px 34px",
  background:
    "linear-gradient(180deg, rgba(42, 31, 23, 0), rgba(42, 31, 23, 0.96) 28%, rgba(42, 31, 23, 0.98))",
  zIndex: 4,
};

const briefingPrimaryButton: React.CSSProperties = {
  ...primaryButton,
  padding: "14px 34px",
  fontSize: 14,
};

const briefingSecondaryButton: React.CSSProperties = {
  ...secondaryButton,
  padding: "14px 30px",
  fontSize: 14,
};

const briefingDisabledButton: React.CSSProperties = {
  ...briefingSecondaryButton,
  opacity: 0.38,
  cursor: "default",
};
